import { useMemo } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import SEO, { firmJsonLd } from "@/components/SEO";
import AnnouncementsReel from "@/components/AnnouncementsReel";
import heroBg from "@/assets/hero-bg.jpg";
import { Link } from "react-router-dom";
import { practiceAreas } from "@/data/practiceAreas";
import { offices } from "@/data/offices";

const Index = () => {
  return (
    <Layout>
      <SEO
        title="Vaughan McLean | Litigation & Advisory Law Firm"
        description={`National law firm with 40+ attorneys across ${offices.length} offices delivering practical litigation and advisory services.`}
        jsonLd={firmJsonLd}
      />
      {/* Full-screen Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img
          src={heroBg}
          alt=""
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 text-center px-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-2xl tracking-[0.2em] uppercase font-body font-light text-[hsl(40,56%,54%)] mb-6"
          >
            Reaching New Heights
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-6xl md:text-[8rem] lg:text-[10rem] font-heading font-semibold text-white leading-none tracking-[0.2em]"
          >
            VAUGHAN MCLEAN
          </motion.h1>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <a href="#about" className="text-muted-foreground hover:text-primary transition-colors">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-px h-12 bg-primary/50 mx-auto"
            />
          </a>
        </motion.div>
      </section>

      <AnnouncementsReel />


      {/* About Preview Section */}
      <section id="about" className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-xs tracking-[0.3em] uppercase font-body text-primary mb-4">Who We Are</p>
              <h2 className="font-heading text-4xl md:text-5xl font-light text-foreground mb-6">
                A Legacy of Excellence
              </h2>
              <p className="text-muted-foreground font-body leading-relaxed mb-6">
                For decades, our firm has been at the forefront of complex litigation and
                corporate law. We combine deep legal expertise with unwavering dedication to our
                clients' success, delivering results that set the standard in the industry.
              </p>
              <Link
                to="/our-story"
                className="inline-block text-xs tracking-[0.2em] uppercase font-body text-primary border-b border-primary pb-1 hover:text-foreground hover:border-foreground transition-colors"
              >
                Learn Our Story
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-8"
            >
              {[
                { number: "24/7", label: "Client Support", link: "/locations" },
                { number: "40+", label: "Attorneys & Counting", link: "/team" },
                { number: "100+", label: "Industries Served", link: "/services" },
                { number: String(offices.length), label: "Office Locations", link: "/locations" },
              ].map((stat) => {
                const content = (
                  <>
                    <p className="font-heading text-4xl text-primary mb-2">{stat.number}</p>
                    <p className="text-xs tracking-[0.15em] uppercase font-body text-muted-foreground">
                      {stat.label}
                    </p>
                  </>
                );
                return stat.link ? (
                  <Link key={stat.label} to={stat.link} className="text-center p-6 border border-border hover:bg-secondary hover:border-primary transition-colors duration-300 cursor-pointer">
                    {content}
                  </Link>
                ) : (
                  <div key={stat.label} className="text-center p-6 border border-border">
                    {content}
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-card py-24 border-t border-border">
        <div className="container mx-auto px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase font-body text-primary mb-4">Practice Areas</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-foreground">
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
            {(() => {
              const topAreas = [...practiceAreas]
                .sort((a, b) => b.attorneySlugs.length - a.attorneySlugs.length)
                .slice(0, 6)
                .sort((a, b) => a.name.localeCompare(b.name));
              return topAreas.map((area, i) => (
                <motion.div
                  key={area.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card p-10 group cursor-pointer hover:bg-secondary transition-colors duration-300"
                >
                  <Link to={`/services#${area.slug}`} className="block">
                    <h3 className="font-heading text-xl text-foreground group-hover:text-primary transition-colors">
                      {area.name}
                    </h3>
                  </Link>
                </motion.div>
              ));
            })()}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block text-xs tracking-[0.2em] uppercase font-body text-primary border-b border-primary pb-1 hover:text-foreground hover:border-foreground transition-colors"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark py-24 border-t border-border">
        <div className="container mx-auto px-8 lg:px-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-6xl font-light text-foreground mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground font-body max-w-2xl mx-auto mb-8">
              Contact us today to discuss how our experienced team can help you navigate your legal challenges.
            </p>
            <Link
              to="/locations"
              className="inline-block px-10 py-4 bg-primary text-primary-foreground font-body text-xs tracking-[0.2em] uppercase hover:bg-primary/90 transition-colors"
            >
              Contact Us
            </Link>
            <p className="text-muted-foreground font-body my-6 text-sm italic">— or —</p>
            <p className="text-muted-foreground font-body mb-6 max-w-xl mx-auto">
              Fill out our Client Intake Form and one of our attorneys will reach out to assist you.
            </p>
            <Link
              to="/intake"
              className="inline-block px-10 py-4 border border-primary text-primary font-body text-xs tracking-[0.2em] uppercase hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              Client Intake
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
